import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { ArrowRight } from 'react-feather';
import i18n from 'i18next';

import HomeLayout from 'layouts/HomeLayout';
import SectionHeading from 'components/ui/SectionHeading';
import ProductCard from 'components/cards/ProductCard';
import NewsCard from 'components/cards/NewsCard';
import { types as newsTypes } from 'constants/data/newsTypes';

// actions
import { getCoverList } from 'context/actions/cover';
import { getProductList } from 'context/actions/product';
import { getCategoriesList } from 'context/actions/categories';
import { getNewsList } from 'context/actions/news';

// category names хэлбэр: { langCode, name }
const localName = (names = [], fallback = '') =>
    names.find((n) => n.langCode === i18n.language)?.name ||
    names.find((n) => n.langCode === 'mn')?.name ||
    fallback;

const btnPrimary =
    'inline-flex items-center justify-center gap-2 h-11 px-7 rounded-md bg-ink text-paper text-[11px] font-semibold uppercase tracking-[0.2em] hover:bg-cocoa transition-colors';

// ---- Hero ------------------------------------------------------------

const Hero = ({ covers, categories, shopHref }) => {
    const isEn = i18n.language !== 'mn';
    const [index, setIndex] = useState(0);
    const slides = covers.length ? covers : [null];

    useEffect(() => {
        if (slides.length < 2) return;
        const timer = setInterval(
            () => setIndex((prev) => (prev + 1) % slides.length),
            6000,
        );
        return () => clearInterval(timer);
    }, [slides.length]);

    useEffect(() => {
        if (index >= slides.length) setIndex(0);
    }, [slides.length, index]);

    const cover = slides[index];
    // Зураг: баазын cover (files.host + path). Cover байхгүй үед л эхний ангиллын
    // зураг, тэр ч байхгүй бол брэндийн зургийг харуулна
    const heroImage = cover?.image || categories[0]?.flagUrl || '/vision.jpg';
    const heading = cover?.title || 'Sor Cashmere';
    // Тайлбар: "Бидний тухай" хуудасны эхний өгүүлбэр (үг үсгээр нь)
    const description =
        cover?.subTitle ||
        (isEn
            ? 'Sor Cashmere LLC was founded in 1998 with the initial objective of raw‐material procurement, domestic and international trade, and coarse‐fiber dehairing.'
            : 'Сор Кашемир ХХК нь 1998 онд анх түүхий эд бэлтгэл, гадаад дотоод худалдаа, хялгас самнах үйлдвэрлэл явуулах зорилготойгоор байгуулагдсан.');

    return (
        // Өндөр: утсанд 480px-ээс, desktop-д 690px-ээс багагүй, дээд тал нь 720px
        <section className="relative h-[70vh] min-h-[480px] sm:min-h-[690px] max-h-[720px] bg-sand overflow-hidden">
            <Image
                key={cover?.id ?? heroImage}
                src={heroImage}
                alt={heading}
                fill
                priority
                sizes="100vw"
                className="object-cover object-top"
            />

            {/* Текст доод зүүн буланд; gradient-гүй тул зөвхөн текстийн ард зөөлөн цайвар сүүдэр */}
            <div className="relative h-full max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14 sm:pb-20">
                <h1 className="font-serif font-semibold uppercase text-[36px] sm:text-[52px] lg:text-[60px] leading-[1.05] tracking-[0.02em] text-ink max-w-2xl [text-shadow:0_1px_16px_rgba(248,246,241,0.9)]">
                    {heading}
                </h1>
                <p className="mt-5 text-[14px] sm:text-[15px] text-ink/80 leading-relaxed max-w-md [text-shadow:0_1px_10px_rgba(248,246,241,0.95)]">
                    {description}
                </p>
                <div className="mt-8">
                    <Link href={shopHref} className={btnPrimary}>
                        {i18n.t('title.all_see_products')}
                        <ArrowRight size={13} />
                    </Link>
                </div>
            </div>

            {slides.length > 1 && (
                <div className="absolute bottom-6 right-0">
                    <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-end gap-4">
                        {slides.map((slide, i) => (
                            <button
                                key={slide?.id ?? i}
                                type="button"
                                onClick={() => setIndex(i)}
                                aria-label={`Slide ${i + 1}`}
                                className={`text-[11px] tracking-[0.2em] pb-1 border-b transition-colors ${
                                    i === index
                                        ? 'text-ink border-ink'
                                        : 'text-ink/40 border-transparent hover:text-ink'
                                }`}
                            >
                                {String(i + 1).padStart(2, '0')}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

// ---- Values strip (хуучин сайтын бодит зураг: Тогтвортой · Ёс зүйтэй · Байгальд ээлтэй · Нөхөн сэргээгдэх) ----

const ValuesStrip = () => {
    const isEn = i18n.language !== 'mn';
    return (
        // Хуудасны хамгийн доор, footer-ийн өмнө; дээд зайг нь өмнөх хэсэг өгнө
        <section className="pb-14 sm:pb-20">
            <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8">
                {/* Зураг 3:1 хүрээнд; цагаан дэвсгэрийг multiply-аар хуудасны өнгөнд уусгана */}
                <div className="relative mx-auto w-full max-w-[960px] aspect-[3/1]">
                    <Image
                        src={
                            isEn
                                ? '/pages/home/values-en.png'
                                : '/pages/home/values-mn.png'
                        }
                        alt={
                            isEn
                                ? 'Sustainable, Ethical, Nature friendly, Regenerative'
                                : 'Тогтвортой, Ёс зүйтэй, Байгальд ээлтэй, Нөхөн сэргээгдэх'
                        }
                        fill
                        sizes="(max-width: 1024px) 100vw, 960px"
                        className="object-contain mix-blend-multiply"
                    />
                </div>
            </div>
        </section>
    );
};

// ---- Category row (өндөр босоо зураг, доод зүүн буланд цагаан нэр) ----

const CategoryTile = ({ category }) => {
    const name = localName(category.names, category.name);
    return (
        <div className="group relative aspect-[3/4] rounded overflow-hidden bg-sand">
            {category.flagUrl ? (
                <Image
                    src={category.flagUrl}
                    alt={name}
                    fill
                    sizes="(max-width: 1024px) 50vw, 25vw"
                    className="object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
            ) : null}
            {/* Цайвар зурган дээр ч нэр уншигдахуйц байлгах доод талын зөөлөн navy бүдэгрэл */}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/60 to-transparent pointer-events-none" />
            <Link
                href={`/products/${category.id}`}
                aria-label={name}
                className="absolute inset-0 group-hover:bg-ink/10 transition-colors"
            />
            <div className="absolute left-4 bottom-4 sm:left-6 sm:bottom-6 pointer-events-none">
                <Link
                    href={`/products/${category.id}`}
                    className="pointer-events-auto text-paper text-[22px] sm:text-[28px] font-medium leading-none"
                >
                    {name}
                </Link>
            </div>
        </div>
    );
};

const CategoryRow = ({ categories }) => {
    if (!categories.length) return null;
    return (
        // Бусад хэсгүүдтэй ижил босоо зай (py-14/20), ижил контейнер, ижил grid зай
        <section id="collection" className="py-14 sm:py-20 scroll-mt-20">
            <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8">
                {/* Гарчиг: title.category = "Ангилал" */}
                <SectionHeading
                    title={i18n.t('title.category')}
                    className="mb-8"
                />
                {/* Зай 6/8px, булан 4px (rounded) */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5 sm:gap-2">
                    {categories.slice(0, 4).map((category) => (
                        <CategoryTile key={category.id} category={category} />
                    ))}
                </div>
            </div>
        </section>
    );
};

// ---- Heritage band (текст нь "Бидний тухай" хуудасны бодит танилцуулгаас) --------------

const HeritageSection = () => {
    const isEn = i18n.language !== 'mn';
    return (
        <section className="bg-sand">
            <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                    <h2 className="font-serif text-[32px] sm:text-[42px] leading-[1.1] text-ink max-w-md">
                        {isEn ? 'About us' : 'Бидний тухай'}
                    </h2>
                    <p className="mt-5 text-[14px] sm:text-[15px] text-ink/75 leading-relaxed max-w-lg">
                        {isEn
                            ? 'Sor Cashmere LLC was founded in 1998 with the initial objective of raw‐material procurement, domestic and international trade, and coarse‐fiber dehairing. Today, our company maintains a fiber‐sorting facility, a scouring factory, a cashmere‐dehairing factory, a dyeing factory, a wool and cashmere‐yarn spinning mill, and a knitted‐garment manufacturing unit, thereby completing every stage of production from primary raw material to finished product.'
                            : 'Сор Кашемир ХХК нь 1998 онд анх түүхий эд бэлтгэл, гадаад дотоод худалдаа, хялгас самнах үйлдвэрлэл явуулах зорилготойгоор байгуулагдсан. Өдгөө тус компанид ноос ноолуур ангилан ялгах цех, угаах үйлдвэр, ноолуур самнах үйлдвэр, будах үйлдвэр, ноос ноолууран утас ээрэх үйлдвэр, сүлжмэл бэлэн бүтээгдэхүүн үйлдвэрлэх үйлдвэр ажиллаж анхан шатны түүхий эдийг эцсийн бэлэн бүтээгдэхүүн болгох бүх шатны боловсруулалтыг гүйцэтгэж байна.'}
                    </p>
                    <Link
                        href="/info"
                        className="mt-7 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.15em] text-ink border-b border-ink/40 pb-1 hover:border-ink transition-colors"
                    >
                        {i18n.t('title.more')}
                        <ArrowRight size={13} />
                    </Link>
                </div>
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-line">
                    <Image
                        src="/pages/home/standart.jpg"
                        alt={
                            isEn
                                ? 'Sor Cashmere spinning mill'
                                : 'Сор Кашемир ээрэх үйлдвэр'
                        }
                        fill
                        sizes="(max-width: 768px) 100vw, 50vw"
                        className="object-cover"
                    />
                </div>
            </div>
        </section>
    );
};

// ---- Featured collection (API createdAt DESC — сүүлд нэмэгдсэн 4) ------------------

const FeaturedCollection = ({ products, shopHref, lang }) => {
    if (!products.length) return null;
    return (
        <section className="py-14 sm:py-20">
            <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHeading
                    title={i18n.t('title.product')}
                    href={shopHref}
                    linkLabel={i18n.t('title.all_see_products')}
                    className="mb-8"
                />
                {/* Ангиллын картуудтай ижил: зай 6/8px, зургийн булан 4px (rounded) */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5 sm:gap-2">
                    {/* Хамгийн ихдээ 4 — API илүү буцаасан ч дэлгэц дээр 4-өөс хэтрэхгүй */}
                    {products.slice(0, 4).map((product) => (
                        <Link
                            key={product.id}
                            href={`/products/more/${product.id}`}
                            className="block"
                        >
                            <ProductCard
                                {...product}
                                ChosenlangCode={lang}
                                fluid
                                radius="rounded"
                            />
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ---- News (баазын news, type=TIME, сүүлийн 3 — гарчиг title.news = "Мэдээ") ----------

const NewsSection = ({ news }) => {
    const isEn = i18n.language !== 'mn';
    if (!news.length) return null;
    return (
        <section className="pb-14 sm:pb-20">
            <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHeading
                    title={i18n.t('title.news')}
                    href={`/newsList/${newsTypes[0].code}`}
                    linkLabel={i18n.t('title.more')}
                    className="mb-8"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-10">
                    {news.slice(0, 3).map((item) => (
                        <NewsCard
                            key={item.id}
                            item={item}
                            isEn={isEn}
                            sizes="(max-width: 768px) 100vw, 33vw"
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

// ---- Page ---------------------------------------------------------------

const HomePage = () => {
    const system = useSelector((state) => state.system);
    const [covers, setCovers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [news, setNews] = useState([]);

    useEffect(() => {
        getInitData();
    }, []);

    const getInitData = async () => {
        const [coverRes, categoryRes, productRes, newsRes] = await Promise.all([
            getCoverList(),
            getCategoriesList(null, '&fromMenu=1'),
            // Онцлох: API createdAt DESC эрэмбэтэй, хамгийн ихдээ 4
            getProductList('page=1&limit=4'),
            // Мэдээ: сүүлийн 3 (TIME төрөл)
            getNewsList('', 1, 3, newsTypes[0].code),
        ]);

        // Hero: баазын (covers) зурагтай бичлэгүүд. is_active-ийг admin тохируулдаггүй
        // (default 0) тул зөвхөн зураг (files холбоос) байгаа эсэхээр нь шүүнэ.
        if (coverRes.status === 'success')
            setCovers((coverRes.data || []).filter((c) => c.image));
        if (categoryRes.status === 'success')
            setCategories(categoryRes.data || []);
        if (productRes.status === 'success')
            setProducts(productRes.data.list || []);
        if (newsRes.status === 'success') setNews(newsRes.data?.list || []);
    };

    const shopHref = categories[0]?.id
        ? `/products/${categories[0].id}`
        : '#collection';

    return (
        <div className="bg-paper">
            <Hero covers={covers} categories={categories} shopHref={shopHref} />
            <CategoryRow categories={categories} />
            <HeritageSection />
            <FeaturedCollection
                products={products}
                shopHref={shopHref}
                lang={system.lang}
            />
            <NewsSection news={news} />
            <ValuesStrip />
        </div>
    );
};

HomePage.Layout = HomeLayout;

export default HomePage;
